import React, { useEffect, useState } from "react";
import { PaymentElement } from '@stripe/react-stripe-js';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { Loader } from "../loader/loader";
import OrganizationSubscriptionService from "../../utilities/services/organization-subscription-service";
import { OrganizationSubscription } from "../../models/interfaces/organization-subscription";
import { WarningItem } from "../warning-item/warning-item";
import { Organization } from "../../models/interfaces/organization";
import { AddOnId } from "../../models/enumerations/add-on-id";
import BaseCardLayoutActions from "../base-card-layout/base-card-layout-actions";
import { PlanId } from "../../models/enumerations/plan-id";
import { OrganizationStatus } from "../../models/enumerations/organization-status";
import OrganizationService from "../../utilities/services/organization-service";
import { useHistory } from "react-router-dom";
import FunctionService from "../../utilities/services/function-service";
import { useTranslation } from 'react-i18next';
import { PlanTypeLabels } from "../../models/plan-type";
import BillingService, { SubscriptionItemDetails } from "../../utilities/services/billing-service";
import { createLogger } from "../../utilities/logger";

const log = createLogger("ReactivateSubscriptionForm");

interface ReactivationDetails {
  planExists: boolean,
  planType: string;
  planCost: string;
  nextPaymentDate: string;
  paymentCycle: string;
  userAddonExists: boolean;
  userAddonCount: string;
  userAddonAmountCharged: string;
  licenseAddonExists: boolean;
  licenseAddonCount: string;
  licenseAddonAmountCharged: string;
  amountCharged: string;
}

interface ReactivateSubscriptionFormProps {
  isReactivatingPlan: boolean;
  isReactivatingAddOn: boolean;
  organization: Organization;
  returnUrl: string;
  products: any[];
  onBackButtonClick: Function;
}

const ReactivateSubscriptionForm: React.FC<ReactivateSubscriptionFormProps> = ({ isReactivatingPlan, isReactivatingAddOn, returnUrl, organization, products, onBackButtonClick }) => {
  const COMPONENT_CLASS = "c-checkoutform";
  const { state } = useAuthState();
  const stripe = useStripe();
  const elements = useElements();
  const [paymentFailedBanner, setPaymentFailedBanner] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [organizationSubscription, setOrganizationSubscription] = useState<OrganizationSubscription>();
  const [planItems, setPlanItems] = useState<SubscriptionItemDetails[]>([]);
  const [addOnItems, setAddOnItems] = useState<SubscriptionItemDetails[]>([]);
  const [planPriceInfo, setPlanPriceInfo] = useState<{ interval: string; interval_count: number } | null>();
  const [addOnPriceInfo, setAddOnPriceInfo] = useState<{ interval: string; interval_count: number } | null>();
  const [planPrice, setPlanPrice] = useState(0);
  const [addOnPrice, setAddOnPrice] = useState(0);
  const { t } = useTranslation();
  const [subscriptionDetails, setSubscriptionDetails] = useState<ReactivationDetails>({
    planExists: false,
    planType: "",
    planCost: "",
    nextPaymentDate: "",
    paymentCycle: "",
    userAddonExists: false,
    userAddonCount: "",
    userAddonAmountCharged: "",
    licenseAddonExists: false,
    licenseAddonCount: "",
    licenseAddonAmountCharged: "",
    amountCharged: "",
  });
  const history = useHistory();

  useEffect(() => {
    const fetchOrgSub = async () => {
      if (organization.id) {
        try {
          const orgSub = await OrganizationSubscriptionService.getByOrganizationId(organization.id);

          if (orgSub && orgSub.length > 0 && orgSub[0]) {
            setOrganizationSubscription(orgSub[0]);

            if (orgSub[0].stripePlanSubscription) {
              const planDetails = await BillingService.getSubscriptionDetails({
                subscriptionId: orgSub[0].stripePlanSubscription,
              });

              setPlanItems(planDetails.items);

              if (planDetails.items.length > 0) {
                const mainItem = planDetails.items.find((item) =>
                  item.productId === PlanId.Core || item.productId === PlanId.Pro || item.productId === PlanId.Business
                ) || planDetails.items[0];

                setPlanPriceInfo({ interval: mainItem.interval ?? "month", interval_count: mainItem.intervalCount });
                const totalPlanAmount = planDetails.items.reduce((sum, item) => sum + (item.amount * item.quantity / 100), 0);
                setPlanPrice(totalPlanAmount);
              }
            }

            if (orgSub[0].stripeAddOnSubscription) {
              const addOnDetails = await BillingService.getSubscriptionDetails({
                subscriptionId: orgSub[0].stripeAddOnSubscription,
              });

              setAddOnItems(addOnDetails.items);

              if (addOnDetails.items.length > 0) {
                const userAddOnItem = addOnDetails.items.find((item) => item.productId === AddOnId.User.toString());
                if (userAddOnItem) {
                  setAddOnPriceInfo({ interval: userAddOnItem.interval ?? "month", interval_count: userAddOnItem.intervalCount });
                }

                const totalAddOnAmount = addOnDetails.items.reduce((sum, item) => sum + (item.amount * item.quantity / 100), 0);
                setAddOnPrice(totalAddOnAmount);
              }
            }
          }
        } catch (e) {
          log.error("Failed to load organization subscription for reactivation", {
            exception: e,
            organizationId: organization.id,
          });
        }
      }
    };

    fetchOrgSub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmIntent = async (clientSecret: string, customerId: string) => {
    setIsLoading(true);
    if (stripe && elements) {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}${returnUrl}`,
        },
        redirect: "if_required",
      });

      if (error) {
        log.error("Stripe confirmSetup error", {
          exception: error,
          customerId,
        });
        setPaymentFailedBanner(true);
        setIsLoading(false);
        return;
      }

      const paymentMethodId =
        typeof setupIntent.payment_method === "string"
          ? setupIntent.payment_method
          : undefined;

      if (!paymentMethodId) {
        log.warn("Subscription reactivation blocked: no payment method returned from Stripe", {
          organizationId: organization?.id,
        });
        setPaymentFailedBanner(true);
        setIsLoading(false);
        return;
      }

      const planPriceId = isReactivatingPlan
        ? products.find((item: any) => item.product.id === PlanId.Core || item.product.id === PlanId.Pro || item.product.id === PlanId.Business)
          ?.prices.find((item: any) => item.data.interval === "month")?.id
        : undefined;

      const userAddOnPriceId = products.find((item) => item.product.id === AddOnId.User).prices.find((item: any) => item.data.active === true).id;
      const licenseAddOnPriceId = products.find((item) => item.product.id === AddOnId.License).prices.find((item: any) => item.data.active === true).id;

      const existingAddOnItems = isReactivatingAddOn && addOnItems.length > 0
        ? addOnItems.map((item) => ({ price: item.priceId, quantity: item.quantity }))
        : undefined;

      if (!organizationSubscription) {
        log.warn("Subscription reactivation blocked: no organization subscription found", {
          organizationId: organization?.id,
        });
        setPaymentFailedBanner(true);
        setIsLoading(false);
        return;
      }

      try {
        const response = await BillingService.completeCheckoutReactivate({
          customerId,
          paymentMethodId,
          isReactivatingPlan,
          isReactivatingAddOn,
          planPriceId,
          addOnItems: existingAddOnItems,
          userAddOnPriceId,
          licenseAddOnPriceId,
        });

        if (isReactivatingPlan && response.planSubscriptionId) {
          organization.showDeactivatedAccountPlanModal = false;
          organizationSubscription.stripePlanSubscription = response.planSubscriptionId;

          if (!organizationSubscription.stripeAddOnSubscription) {
            const addonResponse = await BillingService.createAddonPlaceholderSubscription({
              email: state.user?.email!,
              userAddOnPriceId,
              licenseAddOnPriceId,
            });

            organizationSubscription.stripeAddOnSubscription = addonResponse.subscriptionId;
          }

          await OrganizationSubscriptionService.save(organizationSubscription);

          subscriptionDetails.planExists = true;
          subscriptionDetails.planType = (organization && organization.plan && organization.plan.value !== undefined) ? PlanTypeLabels[organization.plan.value] : "Core";
          subscriptionDetails.planCost = `$${planPrice.toFixed(2)}`;
          subscriptionDetails.nextPaymentDate = calculateNextPayment(planPriceInfo ?? null);
          subscriptionDetails.paymentCycle = response.planInterval === "year" ? "Yearly" : "Monthly";
        }

        if (isReactivatingAddOn && response.addOnSubscriptionId) {
          organization.showDeactivatedAccountAddOnModal = false;
          organizationSubscription.stripeAddOnSubscription = response.addOnSubscriptionId;
          await OrganizationSubscriptionService.save(organizationSubscription);

          if (response.addOnItems && response.addOnItems.length > 0) {
            const userAddOn = response.addOnItems.find((item) => item.productId === AddOnId.User.toString());
            if (userAddOn && userAddOn.quantity > 0) {
              subscriptionDetails.userAddonExists = true;
              subscriptionDetails.userAddonCount = `${userAddOn.quantity}`;
              subscriptionDetails.userAddonAmountCharged = `$${((userAddOn.amount * userAddOn.quantity) / 100).toFixed(2)}`;
            }

            const licenseAddOn = response.addOnItems.find((item) => item.productId === AddOnId.License.toString());
            if (licenseAddOn && licenseAddOn.quantity > 0) {
              subscriptionDetails.licenseAddonExists = true;
              subscriptionDetails.licenseAddonCount = `${licenseAddOn.quantity}`;
              subscriptionDetails.licenseAddonAmountCharged = `$${((licenseAddOn.amount * licenseAddOn.quantity) / 100).toFixed(2)}`;
            }
          }
        }

        setSubscriptionDetails(subscriptionDetails);
        const previousStatus = organization.status;
        organization.status = OrganizationStatus.Active;
        await OrganizationService.save(organization);

        log.info("Subscription reactivated", {
          organizationId: organization?.id,
          organizationName: organization?.name,
          fromStatus: OrganizationStatus[Number(previousStatus)] ?? previousStatus,
          fromStatusValue: previousStatus,
          toStatus: OrganizationStatus[Number(organization.status)] ?? organization.status,
          toStatusValue: organization.status,
          orgSubId: organizationSubscription?.id,
          planType: subscriptionDetails.planType,
          planCost: subscriptionDetails.planCost,
          paymentCycle: subscriptionDetails.paymentCycle,
        });
      } catch (err) {
        log.error("Failed to complete checkout reactivation", {
          exception: err,
          organizationId: organization?.id,
        });
        setPaymentFailedBanner(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) {
      return;
    }

    const { error: submitError } = await elements.submit();
    if (submitError) {
      log.error("Stripe elements submit error", {
        exception: submitError,
      });
      return;
    }

    setIsLoading(true);

    try {
      const setupResponse = await BillingService.createCheckoutSetupIntent({
        email: state.user?.email!,
        name: `${state.user?.firstName} ${state.user?.lastName}`,
      });

      await confirmIntent(setupResponse.clientSecret, setupResponse.customerId);

      if (state.user && state.user?.email) {
        FunctionService.sendSubscriptionReactivationEmail(state.user.email, subscriptionDetails);
      }
      history.replace(returnUrl);
    } catch (error) {
      log.error("Subscription reactivation failed", {
        exception: error,
        organizationId: organization?.id,
      });
      setPaymentFailedBanner(true);
    } finally {
      setIsLoading(false);
    }
  };

  function calculateNextPayment(priceInfo: { interval: string; interval_count: number } | null) {
    if (!priceInfo) return '...';

    const today = new Date();
    const nextBillingDay = new Date(today);

    if (priceInfo.interval === 'month') {
      nextBillingDay.setMonth(today.getMonth() + priceInfo.interval_count);
    } else if (priceInfo.interval === 'year') {
      nextBillingDay.setFullYear(today.getFullYear() + priceInfo.interval_count);
    } else {
      nextBillingDay.setFullYear(today.getFullYear() + 1);
    }

    if (priceInfo.interval === 'month') {
      while (nextBillingDay.getMonth() !== (today.getMonth() + priceInfo.interval_count) % 12) {
        nextBillingDay.setDate(nextBillingDay.getDate() - 1);
      }
    }

    return `${nextBillingDay.toLocaleString('default', { month: 'short' })} ${nextBillingDay.getDate()}, ${nextBillingDay.getFullYear()}`;
  }

  return (
    <div className={`${COMPONENT_CLASS}`}>
      <Loader isVisible={isLoading} />
      <div className={`${COMPONENT_CLASS}__container pt-20 px-8 md:px-24`} style={{ margin: 0 }}>
        <div style={{ textAlign: "center" }}>
          <h1>{t('reactivateSubscription.updatePaymentTitle')}</h1>
          <p>{t('reactivateSubscription.updatePayment_desc')}</p>
        </div>
        {paymentFailedBanner && (
          <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
            <WarningItem
              iconColor="#E7514F"
              onClose={() => { setPaymentFailedBanner(false); }}
              backgroundColor="#FFE9E5"
              title={t('reactivateSubscription.paymentFailedTitle')}
              text={t('reactivateSubscription.paymentFailed_desc')}
            />
          </div>
        )}
        <div className={`${COMPONENT_CLASS}__summary`}>
          <div className={`${COMPONENT_CLASS}_summary_line`}>
            <div style={{ fontSize: '20px' }}>
              {isReactivatingPlan && isReactivatingAddOn && t('reactivateSubscription.symmioBusinessAddOns')}
              {isReactivatingPlan && !isReactivatingAddOn && t('reactivateSubscription.symmioBusiness')}
              {!isReactivatingPlan && isReactivatingAddOn && t('reactivateSubscription.symmioAddOns')}
            </div>
          </div>
          {isReactivatingPlan && planItems.length > 0 &&
            planItems.map((item) => {
              const productName = products.find((productItem) => productItem.product.id === item.productId)?.product?.data?.name;
              return (
                <div key={item.priceId} className={`${COMPONENT_CLASS}_summary_line`}>
                  <div style={{ fontWeight: 'bold' }}>{productName}</div>
                  <div>${((item.amount * item.quantity) / 100).toFixed(2)}</div>
                </div>
              );
            })
          }
          {isReactivatingAddOn && addOnItems.length > 0 &&
            addOnItems.map((item) => {
              if (item.quantity > 0) {
                const productName = products.find((productItem) => productItem.product.id === item.productId)?.product?.data?.name;
                return (
                  <div key={item.priceId} className={`${COMPONENT_CLASS}_summary_line`}>
                    <div style={{ fontWeight: 'bold' }}>{item.quantity} {productName}</div>
                    <div>${((item.amount * item.quantity) / 100).toFixed(2)}</div>
                  </div>
                );
              }
              return null;
            })
          }
          <hr className={`${COMPONENT_CLASS}_divider`} />
          <div className={`${COMPONENT_CLASS}_summary_line`}>
            <div style={{ fontWeight: 'bold' }}>{t('reactivateSubscription.summary.title')}</div>
            <div>${(planPrice + addOnPrice).toFixed(2)}</div>
          </div>
          <hr className={`${COMPONENT_CLASS}_divider`} />
          {isReactivatingPlan && (
            <div className={`${COMPONENT_CLASS}_summary_line`}>
              <div style={{ fontWeight: 'bold' }}>{t('reactivateSubscription.summary.nextPlanPayment', { planPriceInfo: planPriceInfo ? calculateNextPayment(planPriceInfo) : "..." })}</div>
              <div>${planPrice.toFixed(2)}</div>
            </div>
          )}
          {isReactivatingAddOn && (
            <div className={`${COMPONENT_CLASS}_summary_line`}>
              <div style={{ fontWeight: 'bold' }}>{t('reactivateSubscription.summary.nextAddOnPayment', { planPriceInfo: addOnPriceInfo ? calculateNextPayment(addOnPriceInfo) : "..." })}</div>
              <div>${addOnPrice.toFixed(2)}</div>
            </div>
          )}
          <hr className={`${COMPONENT_CLASS}_divider`} />
          <div className={`${COMPONENT_CLASS}_summary_line`}>
            {t('reactivateSubscription.summary.autorenew_desc', { renewDate: (isReactivatingPlan && planPriceInfo) ? calculateNextPayment(planPriceInfo) : (isReactivatingAddOn && addOnPriceInfo ? calculateNextPayment(addOnPriceInfo) : "...") })}
          </div>
        </div>
        <form onSubmit={handleSubmit} className={`${COMPONENT_CLASS}__form`}>
          <PaymentElement />
        </form>
        <div style={{ fontSize: '12px', fontWeight: '300' }}>
          {t('reactivateSubscription.summary.payAndContinue_desc')}
          <a
            style={{ textDecoration: 'underline' }}
            type="button"
            href="https://www.functionalmovement.com/terms"
            target="_blank" rel="noreferrer">
            {t('reactivateSubscription.summary.termsAndConditions')}
          </a>. {t('reactivateSubscription.summary.cancel_desc')}
        </div>
      </div>
      <BaseCardLayoutActions
        backButton={true}
        submitButton={true}
        onSubmit={handleSubmit}
        submitButtonText={t('buttons.btn_payAndContinue')}
        onBack={onBackButtonClick}
        submitDisabled={isLoading}
        backDisabled={isLoading}
      />
    </div>
  );
}

export default ReactivateSubscriptionForm;